import { Tabs, Dropdown, Menu } from "antd";
import { useMemo, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PageName } from "ui/PageName";
import { ProgressBar } from "ui/ProgressBar";
import { PageTitle } from "./PageTitle";
import styles from "./ProNewListingLayout.module.css";
import { proNewListingMenuList } from "./proNewListingMenuList";
import { getRouteMap } from "./routeMaps";
import { EllipsisIcon } from "icons";
export const ProNewListingLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPreviewMode = searchParams.get("readOnlyPreview");
  const { pathname } = useLocation();
  const { id: tripId } = useParams();
  const tabId = pathname.split("/").filter(Boolean).pop();

  const isEditMode = pathname.includes("/edit/");

  const { id, percent, title, subTitle } = useMemo(() => {
    return proNewListingMenuList.find((tabItem) => tabItem.id === tabId) || proNewListingMenuList[0];
  }, [tabId]);

  const onChange = (key) => {
    if (isPreviewMode || !tripId) return;
    const mode = isEditMode ? searchParams.get("route") : "create";
    const routeFn = getRouteMap()[mode]?.[key];
    if (routeFn) {
      navigate(routeFn({ id: tripId }));
    }
  };

  const tabList = useMemo(() => {
    return proNewListingMenuList.map((item) => ({
      key: item.id,
      label: item.label,
    }));
  }, []);

  const [visibleTabs, setVisibleTabs] = useState([]);
  const tabListRef = useRef([]);

  useEffect(() => {
    const handleResize = () => {
      updateVisibleTabs();
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const updateVisibleTabs = () => {
    const containerWidth = document.querySelector('.ant-tabs')?.offsetWidth;
    let totalWidth = 0;
    const visibleTabs = [];

    tabListRef.current.forEach((tab, index) => {
      totalWidth += tab?.offsetWidth;
      if (totalWidth <= containerWidth) {
        visibleTabs?.push(index + 1);
      }
    });

    setVisibleTabs(visibleTabs);
  };

  const handleDropdownClick = (key) => {
    onChange(key);
  };

  const getDropdownMenu = () => {
    const hiddenTabs = proNewListingMenuList
      .map((item) => item.id)
      .filter((key) => key !== id && !visibleTabs.includes(key));

    return (
      <Menu onClick={(e) => handleDropdownClick(e.key)}>
        {hiddenTabs.map((key) => (
          <Menu.Item key={key}>{proNewListingMenuList?.find((item) => item?.id === key)?.label}</Menu.Item>
        ))}
      </Menu>
    );
  };

  return (
    <div className={styles.layout}>
      <div className={styles.header}>
        {isPreviewMode ? (
          <PageName title="Preview Listing" />
        ) : (
          <>
            {!isEditMode && <ProgressBar percent={percent} />}
            <PageName title={isEditMode ? t("listings.edit_new") : t("listings.create_new")} />
            <Tabs
              activeKey={id}
              items={tabList}
              onChange={onChange}
              tabBarExtraContent={
                visibleTabs.length < proNewListingMenuList.length ? (
                  <Dropdown overlay={getDropdownMenu()} trigger={['click']} className={`ml-xs ${styles.tabsMoreIcon}`}>
                    <EllipsisIcon fill="#ddd" height={20} width={20} />
                  </Dropdown>
                ) : null
              }
            />
          </>
        )}
      </div>
      <div className={styles.body}>
        {!isPreviewMode && <PageTitle title={title} subTitle={subTitle} />}
        <div className={styles.pageContent}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};
